/** @format */

import GetAppIcon from '@mui/icons-material/GetApp';
import {Box, Typography} from '@mui/material';
import * as R from 'ramda';
import {Trans, useTranslation} from 'react-i18next';
import {useLocation, useNavigate} from 'react-router-dom';

import {People} from 'mixpanel-browser';

import {ArrowOutwardOutlined} from '@mui/icons-material';

import theme from 'theme';

import SecondaryMediumButton from 'components/buttons/SecondaryMediumButton';
import Autocomplete from 'components/inputs/AutocompleteField';
import {useMixpanelContext} from 'contexts/MixpanelContext';
import {ChecksWithSensor, detailsPageLink} from 'helpers/checkHelpers';
import {sensorName} from 'helpers/sensor_names';
import {SensorCategoriesBunchType, SensorCategoriesType} from 'types';

import SensorBarBunch from 'components/SensorGroupBar/SensorBarBunch';

import SensorBarCategories from 'components/SensorGroupBar/SensorBarCategories';
import categoriesData from 'pages/Home/Main/Project/data/categories.json';
import {SensorGroupsFromQuery} from 'pages/Home/Main/Project/types/common';

import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import {getBunchData} from './helpers/getBunchData';
import {getCategoriesData} from './helpers/getCategoriesData';
import {getColorScheme} from './helpers/getColorScheme';
import {CategoriesData, SensorOption} from './model';
import {useIssuesNewStyles} from './styles';

interface Props {
  validChecks: ChecksWithSensor[];
  excludedChecks: ChecksWithSensor[];
  needRegionsChecks: ChecksWithSensor[];
  sensorGroups: SensorGroupsFromQuery;
  companyId: string;
}

const IssuesNew = ({
  validChecks,
  excludedChecks,
  needRegionsChecks,
  sensorGroups,
  companyId,
}: Props) => {
  const navigate = useNavigate();
  const classes = useIssuesNewStyles();

  const {t} = useTranslation();
  const location = useLocation();
  const {mixpanel: mixpanelUntyped} = useMixpanelContext();
  const mixpanel = mixpanelUntyped as typeof mixpanelUntyped & {people: People};

  const typedCategoriesData = categoriesData as CategoriesData[];

  const handleClick = (category: SensorCategoriesType) => () => {
    navigate({pathname: '/home/main/category', search: `?name=${category}`});
  };

  const handleClickSetSensors = () => {
    navigate({
      pathname: '/home/project/sensors',
      search: `?companyId=${companyId}&edit=1`,
    });
  };

  const sensorsOptions = () => {
    return validChecks
      .concat(excludedChecks)
      .concat(needRegionsChecks)
      .map(v => v.sensor.name)
      .map(v => ({id: v, title: sensorName(v).replaceAll(' ', ' ')}))
      .sort((a, b) => {
        if (a.title > b.title) return 1;
        if (a.title === b.title) return 0;
        return -1;
      });
  };

  const handleChangeAutocomplete = (value: SensorOption | null) => {
    if (value === null) return;

    mixpanel.track('open_sensor_dashboard_autocomplete_variant', {
      sensor: value.id,
    });

    navigate(detailsPageLink(value.id, value.title));
  };

  const handleStartTypingAutocomplete = () => {
    mixpanel.track('start_typing_dashboard_autocomplete_variant');
  };

  const handleClickDownload = () => {
    mixpanel.people.increment('open_full_report_page_count');
    mixpanel.track('click_status_open_full_report_button');

    navigate(
      {
        pathname: '/home/main/report',
      },
      {
        state: {background: location},
      },
    );
  };

  const preparedBunchData = getBunchData(typedCategoriesData, validChecks);

  const preparedCategoriesData = getCategoriesData({
    validChecks: validChecks,
    sensorGroups,
    descriptions: {
      critical: t('Исправляйте в первую очередь — это самые болезненные для бюджета проблемы'),
      regular: t(
        'Возвращайтесь к ним после критичных — срочности нет, но откладывать надолго не стоит',
      ),
      low: t('Можно пропустить, если нет времени — на ключевые метрики почти не влияют'),
    },
  });

  const renderCategories = () =>
    preparedCategoriesData.map(attrs => (
      <SensorBarCategories key={attrs.title} onClick={handleClick(attrs.name)} {...attrs} />
    ));

  const renderBunch = () =>
    preparedBunchData.map((category, i) => {
      const checks = validChecks.filter(check => category['sensors'].includes(check.sensor.name));

      const totalFound = R.sum(checks.map(v => v.found));
      const totalNew = R.sum(checks.map(v => v.new));
      const isCritical = totalFound > 0 && totalNew > 0;
      const isRegular = totalFound > 0 && totalNew === 0;

      return (
        <SensorBarBunch
          key={i}
          description={t(category['description'])}
          totalFound={totalFound}
          totalNew={totalNew}
          title={t(category['title'])}
          onClick={handleClick(category['name'] as SensorCategoriesBunchType)}
          icon={category['icon']}
          variant={getColorScheme(isCritical, isRegular)}
        />
      );
    });

  const isNoSensorTypes = preparedCategoriesData.length === 0;

  return (
    <>
      <Box mt={12}>
        <Box className={classes.header}>
          <Typography className={classes.headerText}>
            <Trans>Все сенсоры</Trans>
          </Typography>
          <Box className={classes.downloadButtonContainer}>
            <SecondaryMediumButton
              className={classes.downloadButton}
              variant='contained'
              onClick={handleClickDownload}
            >
              <GetAppIcon
                className={classes.downloadButtonIcon}
                fontSize='small'
                htmlColor='#fff'
              />
              <Trans>Отчёт</Trans>
            </SecondaryMediumButton>
            {isNoSensorTypes && (
              <Box className={classes.setSensorBox}>
                <Typography className={classes.setSensorText}>
                  <Trans>Распределите сенсоры для полного функционала</Trans>
                </Typography>
                <PrimaryMediumButton
                  onClick={handleClickSetSensors}
                  className={classes.setSensorsButton}
                >
                  <ArrowOutwardOutlined
                    sx={{
                      fontSize: '16px',
                      margin: theme.spacing(0, 0.5, 0.5, 0),
                    }}
                  />
                  <Trans>Распределить сенсоры</Trans>
                </PrimaryMediumButton>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box mt={6} mb={6}>
        <Autocomplete
          placeholder={t('Поиск по сенсорам')}
          options={sensorsOptions()}
          onChange={handleChangeAutocomplete}
          onStartTyping={handleStartTypingAutocomplete}
        />
      </Box>

      <Box>{isNoSensorTypes ? renderBunch() : renderCategories()}</Box>
    </>
  );
};

export default IssuesNew;
